import React, { useEffect, useState, useRef } from "react";
import { api, API } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Download, Plus, Upload, Trash2, FileText } from "lucide-react";

const CATEGORIES = [
  { v: "pitch", l: "Pitch & Présentation" },
  { v: "term_sheet", l: "Term Sheet" },
  { v: "shareholders", l: "Pacte d'actionnaires" },
  { v: "bylaws", l: "Statuts SPV" },
  { v: "technical", l: "Rapports techniques GPU" },
  { v: "compliance", l: "Conformité AMF / DICI" },
  { v: "other", l: "Autres" },
];
const LEVELS = [
  { v: "public", l: "Public" },
  { v: "registered", l: "Compte requis" },
  { v: "qualified", l: "Qualifié + NDA" },
];

export default function Admin() {
  const { lang } = useI18n();
  const [users, setUsers] = useState([]);
  const [invs, setInvs] = useState([]);
  const [txs, setTxs] = useState([]);
  const [reports, setReports] = useState([]);
  const [genYM, setGenYM] = useState("");
  const [drDocs, setDrDocs] = useState([]);
  const [drNda, setDrNda] = useState([]);
  const [upTitle, setUpTitle] = useState("");
  const [upDesc, setUpDesc] = useState("");
  const [upCat, setUpCat] = useState("pitch");
  const [upLvl, setUpLvl] = useState("registered");
  const [upBusy, setUpBusy] = useState(false);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const load = async () => {
    const [u, i, t, r, d, n] = await Promise.all([
      api.get("/admin/users").catch(() => ({ data: [] })),
      api.get("/admin/investments").catch(() => ({ data: [] })),
      api.get("/admin/transactions").catch(() => ({ data: [] })),
      api.get("/admin/reports").catch(() => ({ data: [] })),
      api.get("/data-room/documents").catch(() => ({ data: [] })),
      api.get("/admin/data-room/nda-log").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data); setInvs(i.data); setTxs(t.data); setReports(r.data);
    setDrDocs(d.data); setDrNda(n.data);
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post(`/admin/investments/${id}/approve`); toast.success("Approuvé"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const fund = async (id) => {
    try { await api.post(`/admin/investments/${id}/fund`); toast.success("Alloué"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const generate = async () => {
    try {
      const ym = genYM.trim() || undefined;
      const { data } = await api.post(`/admin/reports/generate`, ym ? { year_month: ym } : {});
      toast.success(`Reporting généré (${data.created}) — ${data.year_month}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!upTitle.trim()) {
      toast.error("Titre requis");
      return;
    }
    setUpBusy(true);
    let success = 0;
    let fail = 0;
    for (const f of Array.from(files)) {
      const fd = new FormData();
      fd.append("title", files.length === 1 ? upTitle : `${upTitle} — ${f.name}`);
      fd.append("description", upDesc);
      fd.append("category", upCat);
      fd.append("access_level", upLvl);
      fd.append("file", f);
      try {
        await api.post("/admin/data-room/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        success += 1;
      } catch (e) {
        fail += 1;
      }
    }
    setUpBusy(false);
    if (success) toast.success(`${success} document(s) uploadé(s)`);
    if (fail) toast.error(`${fail} échec(s)`);
    if (success) {
      setUpTitle(""); setUpDesc("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      load();
    }
  };

  const deleteDoc = async (id) => {
    if (!window.confirm(`Supprimer le document ${id} ?`)) return;
    try {
      await api.delete(`/admin/data-room/${id}`);
      toast.success("Document supprimé");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="label-divider mb-4">Admin</div>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900" data-testid="admin-title">
        Back-office InvestGPU
      </h1>
      <p className="text-sm text-slate-500 mt-2">{lang === "fr" ? "Gestion des utilisateurs, investissements, transactions, reportings et data room." : "Users, investments, transactions, reports and data room."}</p>

      <Tabs defaultValue="investments" className="mt-10">
        <TabsList className="rounded-sm flex-wrap h-auto">
          <TabsTrigger value="investments" className="rounded-sm" data-testid="tab-investments">Investissements</TabsTrigger>
          <TabsTrigger value="users" className="rounded-sm" data-testid="tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-sm" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="reports" className="rounded-sm" data-testid="tab-reports">Reportings</TabsTrigger>
          <TabsTrigger value="dataroom" className="rounded-sm" data-testid="tab-dataroom">Data Room</TabsTrigger>
        </TabsList>

        <TabsContent value="investments" className="mt-6">
          <Table
            rows={invs}
            columns={[
              { k: "investment_id", l: "ID" },
              { k: "user_id", l: "User" },
              { k: "spv_id", l: "SPV" },
              { k: "amount", l: "Montant", r: (v) => `${v}€` },
              { k: "status", l: "Statut" },
              { k: "created_at", l: "Date", r: (v) => (v || "").slice(0, 10) },
              {
                l: "Actions", render: (row) => (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => approve(row.investment_id)} data-testid={`approve-${row.investment_id}`}>Approuver</Button>
                    <Button size="sm" className="rounded-sm h-7 text-xs bg-emerald-700 hover:bg-emerald-800" onClick={() => fund(row.investment_id)} data-testid={`fund-${row.investment_id}`}>Allouer</Button>
                  </div>
                ),
              },
            ]}
          />
        </TabsContent>

        <TabsContent value="users" className="mt-6">
          <Table rows={users} columns={[
            { k: "user_id", l: "User ID" },
            { k: "email", l: "Email" },
            { k: "name", l: "Nom" },
            { k: "role", l: "Role" },
            { k: "kyc_status", l: "KYC" },
          ]} />
        </TabsContent>

        <TabsContent value="transactions" className="mt-6">
          <Table rows={txs} columns={[
            { k: "session_id", l: "Session", r: (v) => v?.slice(-12) },
            { k: "user_id", l: "User" },
            { k: "spv_id", l: "SPV" },
            { k: "amount", l: "Montant", r: (v) => `${v}€` },
            { k: "payment_status", l: "Payment" },
            { k: "status", l: "Status" },
          ]} />
        </TabsContent>

        <TabsContent value="reports" className="mt-6 space-y-6">
          <div className="bg-white border border-slate-200 rounded-sm p-5 flex flex-col sm:flex-row gap-3 sm:items-end" data-testid="admin-generate-form">
            <div className="flex-1">
              <label className="text-[11px] uppercase tracking-[0.15em] text-slate-600 font-bold mb-2 block">Mois (YYYY-MM)</label>
              <Input
                placeholder="laisser vide = mois précédent"
                pattern="\d{4}-\d{2}"
                value={genYM}
                onChange={(e) => setGenYM(e.target.value)}
                className="rounded-sm font-mono"
                data-testid="admin-genym-input"
              />
              <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mt-1.5">Format YYYY-MM (ex. 2026-01)</div>
            </div>
            <Button onClick={generate} className="rounded-sm bg-slate-900 hover:bg-slate-800 h-10" data-testid="admin-generate-btn">
              <Plus className="h-4 w-4 mr-2" /> Générer reporting (tous SPV)
            </Button>
          </div>
          <Table
            rows={reports}
            columns={[
              { k: "year_month", l: "Période" },
              { k: "spv_id", l: "SPV" },
              { l: "Occupation", render: (r) => `${r.kpis?.occupancy_pct}%` },
              { l: "Uptime", render: (r) => `${r.kpis?.uptime_pct}%` },
              { l: "Distribué", render: (r) => `${Math.round(r.kpis?.monthly_distribution_eur || 0).toLocaleString()}€` },
              { k: "generated_at", l: "Généré le", r: (v) => (v || "").slice(0, 10) },
              {
                l: "PDF", render: (r) => (
                  <a
                    href={`${API}/reports/${r.report_id}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-slate-900"
                    data-testid={`admin-report-pdf-${r.report_id}`}
                  >
                    <Download className="h-3 w-3" /> PDF
                  </a>
                ),
              },
            ]}
          />
        </TabsContent>

        <TabsContent value="dataroom" className="mt-6 space-y-8">
          <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="admin-dataroom-upload">
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-600 font-bold mb-4">Nouveau document</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase tracking-[0.1em] text-slate-500 font-bold mb-1.5 block">Titre</label>
                <Input value={upTitle} onChange={(e) => setUpTitle(e.target.value)} placeholder="Ex. Term Sheet SPV Beta" className="rounded-sm" data-testid="admin-dr-title" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.1em] text-slate-500 font-bold mb-1.5 block">Description</label>
                <Input value={upDesc} onChange={(e) => setUpDesc(e.target.value)} placeholder="Description courte (optionnel)" className="rounded-sm" data-testid="admin-dr-desc" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.1em] text-slate-500 font-bold mb-1.5 block">Catégorie</label>
                <select value={upCat} onChange={(e) => setUpCat(e.target.value)} className="w-full h-10 px-3 border border-slate-300 rounded-sm bg-white text-sm" data-testid="admin-dr-category">
                  {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.1em] text-slate-500 font-bold mb-1.5 block">Niveau d'accès</label>
                <select value={upLvl} onChange={(e) => setUpLvl(e.target.value)} className="w-full h-10 px-3 border border-slate-300 rounded-sm bg-white text-sm" data-testid="admin-dr-level">
                  {LEVELS.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
                </select>
              </div>
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
              className={`mt-4 border-2 border-dashed rounded-sm p-8 text-center transition-colors ${dragOver ? "border-emerald-500 bg-emerald-50" : "border-slate-300 bg-slate-50"}`}
              data-testid="admin-dr-dropzone"
            >
              <Upload className="h-8 w-8 mx-auto text-slate-400" />
              <div className="mt-3 text-sm text-slate-700 font-medium">
                Glissez-déposez vos fichiers ici
              </div>
              <div className="text-xs text-slate-500 mt-1">ou cliquez pour parcourir · 25 Mo max par fichier · multi-fichiers acceptés</div>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => uploadFiles(e.target.files)}
                data-testid="admin-dr-file-input"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={upBusy}
                className="rounded-sm bg-slate-900 hover:bg-slate-800 mt-4"
                data-testid="admin-dr-browse-btn"
              >
                {upBusy ? "Upload en cours…" : "Choisir des fichiers"}
              </Button>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-600 font-bold mb-3">Documents publiés ({drDocs.length})</div>
            <Table
              rows={drDocs}
              columns={[
                { k: "doc_id", l: "ID" },
                { k: "title", l: "Titre" },
                { k: "category", l: "Catégorie" },
                { k: "access_level", l: "Accès" },
                { k: "size_bytes", l: "Taille", r: (v) => v ? `${(v / 1024).toFixed(1)} KB` : "—" },
                { k: "uploaded_at", l: "Date", r: (v) => (v || "").slice(0, 10) },
                {
                  l: "Actions", render: (r) => (
                    <div className="flex gap-2">
                      <Button
                        size="sm" variant="outline" className="rounded-sm h-7 text-xs"
                        onClick={() => deleteDoc(r.doc_id)}
                        data-testid={`admin-dr-delete-${r.doc_id}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" /> Supprimer
                      </Button>
                    </div>
                  ),
                },
              ]}
            />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-slate-600 font-bold mb-3">Journal d'acceptation NDA ({drNda.length})</div>
            <Table
              rows={drNda}
              columns={[
                { k: "user_id", l: "User ID" },
                { k: "email", l: "Email" },
                { k: "name", l: "Nom" },
                { k: "version", l: "Version" },
                { k: "ip_address", l: "IP" },
                { k: "accepted_at", l: "Accepté le", r: (v) => (v || "").replace("T", " ").slice(0, 19) },
              ]}
            />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Table({ rows, columns }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200">
            {columns.map((c, i) => <th key={i} className="py-3 px-4 font-medium">{c.l}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="py-10 text-center text-slate-500">Aucune donnée</td></tr>
          )}
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-slate-100">
              {columns.map((c, j) => (
                <td key={j} className="py-3 px-4 text-slate-800 font-mono text-xs">
                  {c.render ? c.render(row) : (c.r ? c.r(row[c.k]) : row[c.k])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
